import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import axios from "axios";
import SEO, { artworkSchema } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ArtworkDetailPage() {
  const { t, i18n } = useTranslation();
  const { artworkId } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", newsletter: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArtwork();
  }, [artworkId]);

  const fetchArtwork = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/artworks/${artworkId}`);
      setArtwork(res.data);
    } catch (err) {
      console.error("Error fetching artwork:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!artwork) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/artwork-inquiry`, {
        artwork_id: artwork.id,
        artwork_title: artwork.title,
        ...formData,
        locale: i18n.language?.startsWith("en") ? "en" : "it",
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "", newsletter: false });
    } catch (err) {
      setError(t("artwork.form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="w-8 h-8 border border-[#C4A462] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="text-center">
          <p className="text-[#757575] mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{t("artwork.not_found")}</p>
          <Link to="/limited-edition" className="btn-luxury-outline">{t("artwork.back_to_collection")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="artwork-detail-page" style={{ backgroundColor: "#FAF9F6" }}>
      <SEO
        titleKey="seo.limited_edition_title"
        descriptionKey="seo.limited_edition_description"
        path={`/limited-edition/${artwork.id}`}
        image={artwork.image}
        schema={artworkSchema(artwork)}
      />
      {/* Back link */}
      <div className="pt-28 lg:pt-36 pb-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link
            to="/limited-edition"
            data-testid="back-to-collection"
            className="text-xs uppercase tracking-[0.15em] text-[#757575] hover:text-[#C4A462] flex items-center gap-2 transition-colors duration-300"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
          >
            <ArrowLeft strokeWidth={1.25} size={16} /> {t("artwork.back_to_collection")}
          </Link>
        </div>
      </div>

      {/* Artwork Content */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Image */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="lg:col-span-7"
            >
              <div className="sticky top-28">
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-auto max-h-[75vh] object-cover"
                  data-testid="artwork-main-image"
                />
              </div>
            </motion.div>

            {/* Details */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={1}
              className="lg:col-span-5"
            >
              <p
                className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-3"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {artwork.collection === "signature" ? t("artwork.collection_signature") : t("artwork.collection_core")} &bull; {artwork.year}
              </p>

              <h1
                className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                data-testid="artwork-title"
              >
                {artwork.title}
              </h1>

              {/* Edition info */}
              <div className="flex items-center gap-6 mb-8 pb-8 border-b border-[#D8D4CC]/40">
                <div>
                  <p className="text-xs text-[#757575] uppercase tracking-[0.15em]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("artwork.edition")}
                  </p>
                  <p className="text-lg text-[#1A1B1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    {artwork.edition_available}<span className="text-[#757575] font-light">/{artwork.edition_total}</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#757575] uppercase tracking-[0.15em]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("artwork.dimensions")}
                  </p>
                  <p className="text-sm text-[#1A1B1A]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                    {artwork.dimensions}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#757575] uppercase tracking-[0.15em]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                    {t("artwork.technique")}
                  </p>
                  <p className="text-sm text-[#1A1B1A]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>
                    {t("artwork.glass_temperate")}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <p
                  className="text-sm text-[#4A4A4A] leading-relaxed mb-6"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                  data-testid="artwork-description"
                >
                  {artwork.description}
                </p>

                {artwork.artist_note && (
                  <div className="border-l-2 border-[#C4A462] pl-6 mt-6">
                    <p
                      className="text-xs uppercase tracking-[0.15em] text-[#C4A462] mb-2"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                    >
                      {t("artwork.artist_note")}
                    </p>
                    <p
                      className="text-sm text-[#4A4A4A] leading-relaxed italic"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                    >
                      {artwork.artist_note}
                    </p>
                  </div>
                )}
              </div>

              {/* Medium details */}
              <div className="p-6 bg-[#F3F1EC] border border-[#D8D4CC]/40 mb-8">
                <p className="text-xs uppercase tracking-[0.15em] text-[#757575] mb-3" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                  {t("artwork.tech_details")}
                </p>
                <div className="space-y-2 text-sm" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>{t("artwork.technique")}</span>
                    <span className="text-[#1A1B1A]" style={{ fontWeight: 400 }}>{artwork.medium}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>{t("artwork.dimensions")}</span>
                    <span className="text-[#1A1B1A]" style={{ fontWeight: 400 }}>{artwork.dimensions}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>{t("artwork.year")}</span>
                    <span className="text-[#1A1B1A]" style={{ fontWeight: 400 }}>{artwork.year}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A]">
                    <span>{t("artwork.price")}</span>
                    <span className="text-[#C4A462]" style={{ fontWeight: 500 }}>{t("artwork.price_on_request")}</span>
                  </div>
                  <div className="flex justify-between text-[#4A4A4A] pt-2 border-t border-[#D8D4CC]/30">
                    <span>{t("artwork.certificate")}</span>
                    <span className="text-[#1A1B1A]" style={{ fontWeight: 400 }}>{t("artwork.certificate_value")}</span>
                  </div>
                </div>
              </div>

              {/* Certificate notice */}
              <div className="flex items-start gap-3 p-5 bg-[#FDFCF9] border border-[#C4A462]/20 mb-8" data-testid="certificate-notice">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C4A462]/10 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4A462" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
                </div>
                <p
                  className="text-sm text-[#4A4A4A] leading-relaxed"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                  dangerouslySetInnerHTML={{ __html: t("artwork.certificate_notice").replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:500;color:#1A1B1A">$1</strong>') }}
                />
              </div>

              {/* Purchase Inquiry Form */}
              <div className="border border-[#D8D4CC]/40 p-6 bg-white">
                <p
                  className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                >
                  {t("artwork.inquiry_title")}
                </p>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6"
                    data-testid="inquiry-success"
                  >
                    <div className="luxury-divider mx-auto mb-4" />
                    <h3
                      className="text-xl tracking-tighter text-[#1A1B1A] mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {t("artwork.inquiry_success_title")}
                    </h3>
                    <p className="text-sm text-[#4A4A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                      {t("artwork.inquiry_success_subtitle")}
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="btn-luxury-outline mt-4 text-[10px] px-4 py-2"
                      data-testid="inquiry-reset-btn"
                    >
                      {t("artwork.inquiry_new")}
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="artwork-inquiry-form" className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("artwork.form_name")} *
                      </Label>
                      <Input
                        data-testid="inquiry-name-input"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        required
                        className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-10 text-sm"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                          {t("artwork.form_email")} *
                        </Label>
                        <Input
                          type="email"
                          data-testid="inquiry-email-input"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          required
                          className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-10 text-sm"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                          {t("artwork.form_phone")}
                        </Label>
                        <Input
                          data-testid="inquiry-phone-input"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 h-10 text-sm"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-[0.15em] text-[#757575]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                        {t("artwork.form_message")}
                      </Label>
                      <Textarea
                        data-testid="inquiry-message-input"
                        value={formData.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        className="rounded-none border-[#D8D4CC] bg-transparent focus:border-[#C4A462] focus:ring-0 text-sm resize-none"
                        rows={3}
                        placeholder={t("artwork.form_message_placeholder")}
                      />
                    </div>

                    {error && (
                      <p data-testid="inquiry-error" className="text-sm text-red-600">{error}</p>
                    )}

                    <div className="flex items-start gap-3 pt-1">
                      <Checkbox
                        id="inquiry-newsletter"
                        data-testid="inquiry-newsletter-checkbox"
                        checked={formData.newsletter}
                        onCheckedChange={(val) => handleChange("newsletter", !!val)}
                        className="mt-0.5 border-[#D8D4CC] data-[state=checked]:bg-[#C4A462] data-[state=checked]:border-[#C4A462] data-[state=checked]:text-[#FAF9F6] rounded-none"
                      />
                      <Label
                        htmlFor="inquiry-newsletter"
                        className="text-xs text-[#4A4A4A] leading-relaxed cursor-pointer"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                      >
                        {t("artwork.inquiry_newsletter")}
                      </Label>
                    </div>

                    <button
                      type="submit"
                      data-testid="inquiry-submit-btn"
                      disabled={submitting}
                      className="btn-luxury w-full flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                      {submitting ? t("artwork.inquiry_submitting") : t("artwork.inquiry_submit")}
                      <Send strokeWidth={1.25} size={14} />
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
